// The app's top chrome is fixed rather than part of the flow, so an element brought to the top of
// the viewport lands underneath it: scrollIntoView({ block: 'start' }) hides a card's own header
// behind the menu bar and leaves the reader looking at the middle of the block it just opened.
//
// These are the bars that can be up there — the promo banner, the header, and the top menu the
// sidebar-less layout shows — and whichever of them are on screen, the lowest edge among them is
// where the page is actually readable from. Measured rather than hardcoded, because which bars
// are present depends on the layout and on whether the banner is showing.
const FIXED_CHROME = '.header-banner, .header-primary, .top-menu';

const GAP = 16;
const MIN_VISIBLE = 160;

/** How far down the viewport the page is readable, in pixels from its top. */
export function chromeOffset() {
    return Array.from(document.querySelectorAll(FIXED_CHROME)).reduce((lowest, element) => {
        const { position } = window.getComputedStyle(element);
        if (position !== 'fixed' && position !== 'sticky') return lowest;

        const { bottom, height } = element.getBoundingClientRect();
        // A bar the current layout hides measures zero; it is not chrome on this screen.
        if (!height) return lowest;

        return Math.max(lowest, bottom);
    }, 0);
}

/**
 * Bring a block into view, moving the page as little as it takes.
 *
 * Two things it does that scrollIntoView does not: it stops at the fixed chrome rather than
 * underneath it, so the block's own heading is the first thing the reader sees; and it does
 * nothing at all when the block is already readable, because a page that jumps on every click
 * costs the reader their place for no gain.
 *
 * @param {Element|null|undefined} element the block to reveal
 * @param {{gap?: number, minVisible?: number}} options how much air to leave under the chrome,
 *   and how much of the block has to be on screen already for the move to be skipped
 */
export function scrollToBlock(element, { gap = GAP, minVisible = MIN_VISIBLE } = {}) {
    if (!element) return;

    const start = chromeOffset() + gap;
    const rect = element.getBoundingClientRect();
    const wanted = Math.min(minVisible, rect.height);

    if (rect.top >= start && rect.top + wanted <= window.innerHeight) return;

    window.scrollTo({
        top: Math.max(0, window.scrollY + rect.top - start),
        behavior: 'smooth',
    });
}
